import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Speaking', href: '/speaking' },
  { label: 'Board & Advisory', href: '/board-advisory' },
  { label: 'Press & Media', href: '/press-media' },
  { label: 'BuildFit AI', href: '/buildfit-ai' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav
        aria-label="Main navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || mobileOpen
            ? 'bg-white/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 sm:px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group z-10">
            <div className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center bg-gold">
              <span className="text-charcoal font-extrabold tracking-tight select-none" style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '15px', letterSpacing: '-0.02em' }}>JD</span>
            </div>
            <span className={`font-semibold text-sm md:text-base tracking-tight transition-colors ${
              scrolled || mobileOpen ? 'text-charcoal' : 'text-white'
            }`}>
              Jeff Dumè
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-gold whitespace-nowrap ${
                  scrolled ? 'text-charcoal' : 'text-white/90'
                } ${location.pathname === link.href ? 'text-gold' : ''}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3 lg:gap-4">
            <a
              href="https://meetings-na2.hubspot.com/mainframesolutions/buildfitaistrategy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gold text-charcoal text-sm font-semibold px-4 py-2 rounded-md hover:bg-gold-light transition-colors whitespace-nowrap"
            >
              BuildFit AI Strategy Call
              <i className="ri-calendar-line text-sm"></i>
            </a>
            <a
              href="https://www.linkedin.com/in/jdume/"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
                scrolled
                  ? 'bg-charcoal/10 text-charcoal hover:bg-gold hover:text-white'
                  : 'bg-white/15 text-white hover:bg-gold'
              }`}
            >
              <i className="ri-linkedin-fill text-sm"></i>
            </a>
          </div>

          {/* Hamburger button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            className={`md:hidden relative z-10 w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled || mobileOpen ? 'text-charcoal' : 'text-white'
            }`}
          >
            <i className={`text-xl transition-all duration-200 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile menu backdrop */}
      <div
        onClick={() => setMobileOpen(false)}
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden transition-opacity duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Mobile menu panel */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-40 w-[80vw] max-w-[320px] bg-charcoal md:hidden flex flex-col transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 h-16 border-b border-white/10 shrink-0">
          <span className="text-white font-semibold text-sm tracking-wide">Menu</span>
          <button
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto px-6 py-6">
          <ul className="flex flex-col gap-1">
            {navLinks.map((link, index) => (
              <li key={link.href}>
                <Link
                  to={link.href}
                  className={`flex items-center gap-3 px-3 py-3.5 rounded-lg text-base font-medium transition-colors ${
                    location.pathname === link.href
                      ? 'text-gold bg-gold/10'
                      : 'text-white/80 hover:text-white hover:bg-white/5'
                  }`}
                  style={{ transitionDelay: mobileOpen ? `${index * 40}ms` : '0ms' }}
                >
                  {link.label}
                  {location.pathname === link.href && (
                    <i className="ri-arrow-right-s-line text-gold ml-auto"></i>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom CTA */}
        <div className="px-6 py-6 border-t border-white/10 shrink-0 flex flex-col gap-3">
          <a
            href="https://meetings-na2.hubspot.com/mainframesolutions/buildfitaistrategy"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 bg-gold text-charcoal font-semibold text-sm px-5 py-3 rounded-md hover:bg-gold-light transition-colors whitespace-nowrap"
          >
            BuildFit AI Strategy Call
            <i className="ri-calendar-line text-sm"></i>
          </a>
          <a
            href="https://www.linkedin.com/in/jdume/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-white/50 hover:text-gold transition-colors py-2"
          >
            <i className="ri-linkedin-fill text-lg"></i>
            <span className="text-sm">Connect on LinkedIn</span>
          </a>
        </div>
      </div>
    </>
  );
}